import * as yup from 'yup';
import { ValidationMessageKeys, ValidationTFunction } from '@retail/form/i18n';

export interface ValidationError<MessageKeysT extends string> {
  /** key into translation file */
  key: MessageKeysT | ValidationMessageKeys;

  /** token values */
  values?: { [key: string]: any };
}

const testValue = (regex: RegExp) => (value: string | undefined) => {
  if (!value) {
    return true;
  }
  return regex.test(value);
};

export const nameAndNumbersRegex = /^[a-zA-Z0-9 .'\-æøåäö]+$/i;
export const safeTextRegex = /^[\p{L}0-9\s\-_.,&()]*$/u;
export const lettersRegex = /^[a-zæøåäö]+$/i;
export const lettersAndSpaceRegex = /^[a-zæøåäö\s]+$/i;
export const numbersRegex = /^\d+$/;
export const decimalNumberRegex = /^\d+([.,]\d+)?$/;
export const phoneRegex =
  /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;
export const percentageRegex = /^([1-9]?[0-9]((\.|,)\d+)?|100((\.|,)?0+)?)$/;
export const percentageIncludingNegativeRegex = /^(-?)(100|[1-9]\d?([.,]\d+)?|0([.,]\d+)?)$/;
export const numberWithOptionalDecimalRegex = /^(100|[1-9]\d?([.,]\d+)?|0([.,]\d+)?)$/;
export const numberBetween1And2Regex = /^1([.,]\d+)?$|^2([.,]0+)?$|^2$/;
export const maxTwoDecimalsRegex = /^\d+(\.\d{1,2})?$/;

export const nameAndNumbers = <MessageKeysT extends string = ValidationMessageKeys>(
  errorMessage: ValidationError<MessageKeysT> = { key: 'validation.nameAndNumbers' }
) => yup.string().matches(nameAndNumbersRegex, { message: errorMessage });

export const letters = <MessageKeysT extends string = ValidationMessageKeys>(
  errorMessage: ValidationError<MessageKeysT> = { key: 'validation.letters' }
) => yup.string().matches(lettersRegex, { message: errorMessage });

export const lettersAndSpace = <MessageKeysT extends string = ValidationMessageKeys>(
  errorMessage: ValidationError<MessageKeysT> = { key: 'validation.lettersAndSpace' }
) => yup.string().matches(lettersAndSpaceRegex, { message: errorMessage });

export const numberString = <MessageKeysT extends string = ValidationMessageKeys>(
  errorMessage: ValidationError<MessageKeysT> = { key: 'validation.number' }
) => yup.string().matches(numbersRegex, { message: errorMessage });

export const decimalNumberString = <MessageKeysT extends string = ValidationMessageKeys>(
  errorMessage: ValidationError<MessageKeysT> = { key: 'validation.decimalNumber' }
) => yup.string().matches(decimalNumberRegex, { message: errorMessage });

export const phone = <MessageKeysT extends string = ValidationMessageKeys>(
  errorMessage: ValidationError<MessageKeysT> = { key: 'validation.phone' }
) => yup.string().test('isPhone', { message: errorMessage }, testValue(phoneRegex));

const normalizeDecimalSeparator = (value: any) => {
  if (typeof value === 'string') {
    return value.replace(',', '.');
  }
  return value;
};

yup.addMethod(yup.number, 'normalizeDecimalSeparator', function () {
  return this.transform((value, originalValue) => {
    if (typeof originalValue === 'string') {
      const normalized = normalizeDecimalSeparator(originalValue).trim();

      if (!/^-?\d+([.,]\d+)?$/.test(originalValue.trim())) {
        return NaN;
      }

      return parseFloat(normalized);
    }
    return value;
  });
});

yup.addMethod(yup.number, 'maxTwoDecimals', function (errorMessage: string) {
  return this.test('max-two-decimals', errorMessage, function (value) {
    if (value == null) return true;
    return maxTwoDecimalsRegex.test(value.toString());
  });
});

yup.addMethod(yup.number, 'price', function (t: ValidationTFunction) {
  return this.typeError(t('validation.number'))
    .normalizeDecimalSeparator()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .maxTwoDecimals(t('validation.maxTwoDecimals'));
});

declare module 'yup' {
  interface NumberSchema {
    normalizeDecimalSeparator(): this;
    maxTwoDecimals(errorMessage: string): this;
    price(t: ValidationTFunction): this;
  }
}
